import { SEARCH_TYPE_OPTIONS, SEARCH_FILTER_COMPONENT_TYPES, EDUCATION_TYPE_VARIANTS, EDUCATION_BOOKING_VARIANTS } from '@/assets/js/config/server'
import { SEARCH_INITIAL_VALUES, SEARCH_FILTER_EMPTY_CONTROL_VALUES, SEARCH_FILTER_URL_PARAMETER_SEPARATOR, TEASER_IMAGE_TYPES } from '@/assets/js/config/client'
import { PRODUCT_ORDER_STATUS } from '@/constants'

import { getSearchParameters } from '@/assets/js/helper/url'

/** Resolves the Vuex store at call time to avoid a circular import:
 * store/core → modules/search → this file → store/index (was triggering app-definition before Store existed).
 */
function getStore () {
  // eslint-disable-next-line global-require, import/no-dynamic-require
  return require('@/$plugins/store').default
}

const SEARCH = {
  isInitialized: false,
  initialParameters: [],
  urlParameterKeys: [],
  initialOnlyParameterKeys: []
}

export function filterIdGenerator (filter = {}) {
  return `${window.location.pathname}:${filter.field}`
}

export function filterControlIdGenerator (filter = {}, control = {}) {
  return `${filter.field}_${control.key}`
}

export function sortIdGenerator (sort = {}) {
  return `${window.location.pathname}:${sort.field}_${sort.isDescending ? 'descending' : 'ascending'}`
}

export function filterMapper (filter = {}, existingFilter = {}) {
  const COMPONENT = (Object.values(SEARCH_FILTER_COMPONENT_TYPES).find(t => t.type === filter.type) || {}).type || SEARCH_FILTER_COMPONENT_TYPES.unknown.type
  const INITIAL_PARAMETER_VALUES = (SEARCH.initialParameters.find(p => p.key === filter.key) || {}).values || []

  return {
    id: filterIdGenerator(filter),
    field: filter.field,
    key: filter.key,
    type: filter.type,
    component: `filter-${COMPONENT}`,
    sortOrder: filter.sortOrder,
    displayName: (filter.displayName || {})[getStore().getters['gui/getLanguage']],
    controls: getControls()
  }

  function getControls () {
    let controls = [{ unknown: `The type '${filter.type}' of filter '${filter.field}' is unknown! You may want to define mapping logic for this type of filters in '@/assets/js/helper/search.js'.` }]

    if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.text.type) {
      const EXISTING_CONTROL = (existingFilter.controls || [])[0] || {}
      const CONTROL_VALUE = INITIAL_PARAMETER_VALUES[0] || SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type]

      controls = [Object.assign({
        id: filterControlIdGenerator(filter),
        value: CONTROL_VALUE
      }, EXISTING_CONTROL)]
    } else if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.multiselect.type) {
      controls = filter.items
        .map(item => {
          const CONTROL_ID = filterControlIdGenerator(filter, item)
          const EXISTING_CONTROL = (existingFilter.controls || []).find(c => c.id === CONTROL_ID) || {}
          const matchesInitialValue = INITIAL_PARAMETER_VALUES.some(v => {
            const normalizedValue = String(v || '').trim().toLowerCase()
            if (!normalizedValue) return false

            return getComparableItemValues(item)
              .some(itemValue => itemValue === normalizedValue)
          })
          // Keep existing selection state (e.g. after "Mehr laden") and only fall back to initial URL/initial-filters.
          const CONTROL_VALUE = EXISTING_CONTROL.value !== undefined
            ? EXISTING_CONTROL.value
            : (matchesInitialValue ? true : SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type])

          return Object.assign({
            id: CONTROL_ID,
            key: item.key,
            text: item.displayName || item.key,
            value: CONTROL_VALUE,
            hide: false
          }, EXISTING_CONTROL, { count: item.count })
        })
    } else if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.daterange.type) {
      controls = Object.keys(SEARCH_FILTER_COMPONENT_TYPES.daterange.controlKeys)
        .map((dKey, dIndex) => {
          const CONTROL_ID = filterControlIdGenerator(filter, { key: dKey })
          const EXISTING_CONTROL = (existingFilter.controls || []).find(c => c.id === CONTROL_ID) || {}
          const CONTROL_VALUE = INITIAL_PARAMETER_VALUES[dIndex] || SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type]

          return Object.assign({
            id: CONTROL_ID,
            key: dKey,
            value: CONTROL_VALUE
          }, EXISTING_CONTROL)
        })
    } else if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.range.type) {
      controls = [{ key: 'minValue', value: filter.minValue }, { key: 'maxValue', value: filter.maxValue }]
        .map((control, cIndex) => {
          const CONTROL_ID = filterControlIdGenerator(filter, control)
          const EXISTING_CONTROL = (existingFilter.controls || []).find(c => c.id === CONTROL_ID) || {}
          const CONTROL_VALUE = INITIAL_PARAMETER_VALUES[cIndex] || control.value

          return Object.assign({
            id: CONTROL_ID,
            key: control.key,
            type: control.key,
            value: CONTROL_VALUE,
            baseValue: control.value,
            [control.key]: control.value
          }, EXISTING_CONTROL)
        })
    }

    controls.forEach(c => { if (c.unknown) console.warn(c.unknown) })

    return controls
  }
}

function getComparableItemValues (item = {}) {
  const values = []
  const pushNormalized = (value) => {
    if (typeof value !== 'string') return
    const normalizedValue = value.trim().toLowerCase()
    if (normalizedValue) values.push(normalizedValue)
  }

  Object.values(item).forEach(value => {
    if (typeof value === 'string') {
      pushNormalized(value)
      return
    }

    if (Array.isArray(value)) {
      value.forEach(v => pushNormalized(v))
      return
    }

    if (value && typeof value === 'object') {
      Object.values(value).forEach(v => pushNormalized(v))
    }
  })

  return values
}

export function sortMapper (sort = {}, existingSort = {}) {
  return Object.assign({
    id: sortIdGenerator(sort),
    field: sort.field,
    sortOrder: sort.sortOrder,
    isDefault: sort.isDefault,
    isDescending: sort.isDescending,
    displayName: (sort.displayName || {})[getStore().getters['gui/getLanguage']],
    isActive: sort.isDefault
  }, existingSort)
}

export function filterControlResetter (filter = {}) {
  return filter.controls
    .map(control => {
      if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.multiselect.type) control.value = SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type]
      else control.value = SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type]

      return control
    })
}

export function searchParameters ({ filters = [], sorts = [], statistics = {} }) {
  return {
    filters: filterParameters(filters),
    sorts: sortParameters(sorts),
    page: pageParameters(statistics)
  }
}

export function filterParameters (filters = []) {
  const filterParameterBase = !SEARCH.isInitialized ? initialParameterGetter() : filters
  SEARCH.isInitialized = true

  const mappedFilters = filterParameterBase
    .map(filter => {
      return {
        key: filter.key,
        field: filter.field,
        values: getFilterValues(filter)
      }
    })
    .filter(filter => filter.values.length > 0)

  // Keep initial filters (e.g. category from page context) if the backend response
  // does not expose that filter anymore. This prevents losing the constraint on paging.
  const mappedKeys = mappedFilters.map(f => f.key)
  const missingInitialFilters = initialParameterGetter()
    .filter(initialFilter => !mappedKeys.includes(initialFilter.key))
    .map(initialFilter => ({
      key: initialFilter.key,
      values: (initialFilter.values || []).filter(v => v !== null && v !== undefined && String(v).trim() !== '')
    }))
    .filter(filter => filter.values.length > 0)

  return mappedFilters.concat(missingInitialFilters)

  function getFilterValues (filter = {}) {
    // this is for allready mapped filters
    if (filter.controls) {
      return filter.controls
        .filter(c => {
          if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.daterange.type) { return filter.controls.some(fc => fc.value) }
          if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.range.type) { return filter.controls.some(c => c.value !== c.baseValue) }
          return c.value !== SEARCH_FILTER_EMPTY_CONTROL_VALUES[filter.type]
        })
        .map(c => {
          if (filter.type === SEARCH_FILTER_COMPONENT_TYPES.multiselect.type) return c.key
          return c.value
        })
        .flat()

      // this is for initial filterParameters
    } else if (filter.values) {
      return filter.values.map(v => v || null)
    } else {
      return []
    }
  }
}

export function sortParameters (sorts = []) {
  return sorts.length <= 0 ? null : sorts
    .filter(sort => sort.isActive)
    .map(sort => ({
      field: sort.field,
      descending: sort.isDescending
    }))
}

export function pageParameters (statistics = {}) {
  const pageStats = statistics.page || {}

  return {
    number: pageStats.number || SEARCH_INITIAL_VALUES.page,
    size: pageStats.size || SEARCH_INITIAL_VALUES.pageSize
  }
}

export function initialParameterGetter () {
  return SEARCH.initialParameters
}

export function initialParameterSetter (initialParameters = []) {
  const initialUrlParameters = getBaseUrlParameters()

  const PARAMETER_KEYS = Array.from(new Set([].concat(initialUrlParameters, initialParameters).map(p => p.key)))
  const INITIAL_PARAMETERS = PARAMETER_KEYS.map(pKey => initialUrlParameters.find(p => p.key === pKey) || initialParameters.find(p => p.key === pKey))
  const INITIAL_URL_PARAMETER_KEYS = initialUrlParameters.map(p => p.key)

  SEARCH.initialOnlyParameterKeys = initialParameters
    .map(p => p.key)
    .filter(pKey => !INITIAL_URL_PARAMETER_KEYS.includes(pKey))

  urlParameterKeySetter(INITIAL_PARAMETERS)
  SEARCH.initialParameters = INITIAL_PARAMETERS
    .map(f => ({
      key: f.key,
      values: f.values || []
    }))
}

export function urlParameterGetter () {
  return getBaseUrlParameters()
    .filter(p => SEARCH.urlParameterKeys.includes(p.key))
}

export function urlParameterKeySetter (filterParameters = []) {
  SEARCH.urlParameterKeys = filterParameters
    .map(f => f.key)
}

export function urlParameterSetter (filterParameters = []) {
  const mergedParameters = getBaseUrlParameters()
    .filter(p => !SEARCH.urlParameterKeys.includes(p.key))
    .concat(filterParameters.filter(p => !SEARCH.initialOnlyParameterKeys.includes(p.key)))
    .reduce((result, parameter) => {
      const existingParameter = result.find(p => p.key === parameter.key)
      const parameterValues = (parameter.values || [])
        .map(v => String(v || '').trim())
        .filter(v => v)

      if (existingParameter) {
        existingParameter.values = Array.from(new Set(existingParameter.values.concat(parameterValues)))
      } else {
        result.push({
          key: parameter.key,
          values: parameterValues
        })
      }

      return result
    }, [])

  const URL_PARAMETERS = mergedParameters
    .reduce((url, p) => `${url}&${p.key}=${p.values.join(SEARCH_FILTER_URL_PARAMETER_SEPARATOR)}`, '')
    .replace(/^&/, '?')

  window.history.replaceState(null, null, encodeURI(URL_PARAMETERS) || window.location.pathname)
}

export function fieldToKeyConverter (field = '') {
  return field.replace(/(-[a-z]{2})?\..*$/, '')
}

export function resultMapper (results = [], searchType) {
  resultMapper.product = productMapper
  resultMapper.document = documentMapper

  return results.map(resultMapper[SEARCH_TYPE_OPTIONS[searchType].resultType] || ((r) => r))

  function productMapper (r) {
    const purchaseInfo = mapPurchaseInfo(r)
    const marketingMeta =
      searchType === 'marketingproduct'
        ? {
          isStoreWindow: !!(r.isStoreWindow ?? r.IsStoreWindow),
          bundleCount: (r.bundleCount ?? r.BundleCount) != null && (r.bundleCount ?? r.BundleCount) !== ''
            ? String(r.bundleCount ?? r.BundleCount)
            : ''
        }
        : null

    const result = {
      id: (r.externalId || r.id || '').toString(),
      title: r.name,
      text: r.teaserText,
      href: getStore().getters['gui/getBaseUrl'](searchType, r.urlSlug),
      searchType,
      marketing: marketingMeta,
      noImg: SEARCH_TYPE_OPTIONS[searchType].resultNoImg,
      imgType: TEASER_IMAGE_TYPES.includes(searchType) ? searchType : null,
      imgSrc: r.image,
      imgAlt: r.imageAlt,
      product: {
        portioning: r.portionierung
      },
      originalItem: r,
      purchase: purchaseInfo,
      event: {
        id: r.id,
        type: r.eventTypeName,
        typeVariant: EDUCATION_TYPE_VARIANTS[r.eventTypeId],
        date: r.date,
        from: r.from,
        to: r.to,
        location: r.locationPlace,
        language: (r.language || '').replace('-', ''),
        state: r.bookingTypeName,
        stateVariant: EDUCATION_BOOKING_VARIANTS[r.bookingTypeId]
      },
      alternativeEvents: (r.alternativeEvents || [])
        .filter(e => e.date && e.locationPlace)
        .map(e => ({
          id: e.id,
          date: e.date,
          from: e.from,
          to: e.to,
          location: e.locationPlace,
          url: getStore().getters['gui/getBaseUrl'](searchType, e.urlSlug)
        })),
      approvals: r.approvals,
      showAlternativeEvents: false,
      toggleAlternativeEvents () {
        result.showAlternativeEvents = !result.showAlternativeEvents
      }
    }

    return result
  }

  function mapPurchaseInfo (r) {
    const orderStatus = getOrderStatus(r)
    const hasEligibilityData = typeof r.isEligibleForPurchase === 'boolean' || r.stockAmount !== undefined
    const hasPurchaseData = hasEligibilityData || orderStatus !== null || hasAnyPriceField(r)

    return {
      showPurchaseInfo: hasPurchaseData,
      hasEligibilityData,
      isEligibleForPurchase: r.isEligibleForPurchase,
      stockAmount: Number(r.stockAmount || 0),
      orderStatus: orderStatus || PRODUCT_ORDER_STATUS.LOCATION_REQUIRED,
      priceinfo: {
        currency: r.currency || 'CHF',
        price: getPrice(r),
        priceUnassembled: Number(r.priceUnassembled || 0),
        priceOriginal: Number(r.priceOriginal || 0)
      }
    }
  }

  function getOrderStatus (r) {
    if (typeof r.isEligibleForPurchase === 'boolean') {
      if (!r.isEligibleForPurchase) return PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY
      return Number(r.stockAmount || 0) > 0
        ? PRODUCT_ORDER_STATUS.AVAILABLE
        : PRODUCT_ORDER_STATUS.OUT_OF_STOCK
    }

    const rawStatus = r.orderStatus || r.productStatus || r.status
    if (!rawStatus) return null

    const normalizedStatus = rawStatus.toString().toLowerCase()
    if (normalizedStatus === 'available') return PRODUCT_ORDER_STATUS.AVAILABLE
    if (normalizedStatus === 'outofstock' || normalizedStatus === 'out_of_stock') return PRODUCT_ORDER_STATUS.OUT_OF_STOCK
    if (normalizedStatus === 'locationrequired' || normalizedStatus === 'location_required') return PRODUCT_ORDER_STATUS.LOCATION_REQUIRED
    if (normalizedStatus === 'notorderableeligibility' || normalizedStatus === 'not_orderable_eligibility') return PRODUCT_ORDER_STATUS.NOT_ORDERABLE_ELIGIBILITY

    return null
  }

  function getPrice (r) {
    if (r.exFactoryPrice !== undefined && r.exFactoryPrice !== null) return Number(r.exFactoryPrice)
    if (r.price !== undefined && r.price !== null) return Number(r.price)

    const exFactory = (r.prices || []).find(p => (p.type || '').toLowerCase() === 'exfactory')
    if (exFactory?.price !== undefined && exFactory?.price !== null) return Number(exFactory.price)

    return 0
  }

  function hasAnyPriceField (r) {
    return r.price !== undefined ||
      r.priceOriginal !== undefined ||
      r.priceUnassembled !== undefined ||
      Array.isArray(r.prices)
  }

  function documentMapper (r) {
    return r
  }
}

function getBaseUrlParameters () {
  return getSearchParameters()
    .map(p => ({
      key: p.key,
      values: p.value.split(SEARCH_FILTER_URL_PARAMETER_SEPARATOR)
    }))
}

export default {
  filterIdGenerator,
  filterControlIdGenerator,
  sortIdGenerator,
  filterMapper,
  sortMapper,
  filterControlResetter,
  searchParameters,
  filterParameters,
  sortParameters,
  pageParameters,
  initialParameterGetter,
  initialParameterSetter,
  urlParameterKeySetter,
  urlParameterGetter,
  urlParameterSetter,
  fieldToKeyConverter,
  resultMapper
}
